/**
 * O que cada domínio de achados diz de si: título, subtítulo, o que mostrar quando não há nada, e
 * como nomear a via da evidência.
 *
 * Separado das frases porque muda por outra razão: a frase é sobre UMA regra, isto é sobre o painel
 * inteiro. Domínio novo é uma entrada aqui e um punhado de frases lá.
 */
import { MapPin, Landmark, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type DominioAchados = {
  icone: LucideIcon;
  titulo: string;
  subtitulo: string;
  /** O texto de "nada aqui" — diz por que pode estar vazio, não só que está. */
  vazio: string;
  /** Como apresentar os achados fracos, que ficam separados embaixo. */
  fracos: { rotulo: (n: number) => string; explicacao: string };
  /** Como a afirmação foi feita — e o quanto ela vale. Via ausente daqui não vira etiqueta. */
  vias: Record<string, { rotulo: string; classe: string }>;
  /** Botão que dispara o trabalho que produz os achados, quando o domínio tem um. */
  acao?: { rotulo: string; endpoint: string; sucesso: string };
};

export const DOMINIOS: Record<string, DominioAchados> = {
  rastreamento: {
    icone: MapPin,
    titulo: 'O que o trajeto revelou',
    subtitulo:
      'Paradas e passagens cruzadas com quem já está no cadastro. Atualiza sozinho a cada 20 ' +
      'minutos.',
    vazio:
      'Nada cruzou com o cadastro ainda. Ou o veículo não parou em endereço conhecido, ou a ' +
      'análise ainda não passou por estas posições.',
    fracos: {
      rotulo: (n) => `Possíveis, só pelo nome da rua (${n})`,
      explicacao:
        'Uma rua tem quilômetros. Isto aqui é pista para conferir, não afirmação — e por isso ' +
        'não entra no grafo.',
    },
    // Casar por coordenada é medida; por CEP é a quadra; por nome de rua é um palpite. Mostrar os
    // três com a mesma cara faria a ferramenta mentir com confiança.
    vias: {
      coordenada: { rotulo: 'no ponto', classe: 'border-success/40 text-success' },
      cep: { rotulo: 'mesmo CEP', classe: 'border-border text-text-secondary' },
      logradouro: { rotulo: 'mesma rua', classe: 'border-warning/40 text-warning' },
    },
    acao: {
      rotulo: 'Analisar agora',
      endpoint: '/cadastro/rastreadores/analisar',
      sucesso: 'Análise enfileirada — os achados aparecem em instantes.',
    },
  },

  parentesco: {
    icone: Users,
    titulo: 'Parentesco que ninguém digitou',
    subtitulo:
      'Laços de família derivados do que já está na base — filiação, sobrenome, endereço e ' +
      'patrimônio. Nada aqui foi gravado: é leitura, e quem confirma é você, na ficha.',
    vazio:
      'Nenhum laço derivado ainda. O mais comum é a ficha não declarar mãe nem pai — de 71.315 ' +
      'pessoas da base, 2 declaram. É o que o enriquecimento por fontes públicas muda.',
    fracos: {
      rotulo: (n) => `Pistas de família, para conferir (${n})`,
      explicacao:
        'Nome de mãe não identifica ninguém: «Maria da Silva» casa com dezenas. Sobrenome e ' +
        'endereço em comum podem ser cônjuge, ou coincidência. É motivo para perguntar, não ' +
        'afirmação — e por isso não entra no grafo.',
    },
    // A via separa o que é REGISTRO do que é SEMELHANÇA. Mostrar as duas com a mesma cara faria a
    // ferramenta afirmar parentesco por coincidência de nome.
    vias: {
      mae_no_grafo: { rotulo: 'mesma mãe no cadastro', classe: 'border-success/40 text-success' },
      parente_declarado: {
        rotulo: 'parente declarado',
        classe: 'border-success/40 text-success',
      },
      nome_da_mae: { rotulo: 'nome da mãe', classe: 'border-warning/40 text-warning' },
      sobrenome_e_endereco: {
        rotulo: 'sobrenome + endereço',
        classe: 'border-warning/40 text-warning',
      },
      sobrenome_raro: { rotulo: 'sobrenome raro', classe: 'border-warning/40 text-warning' },
    },
  },

  credito: {
    icone: Landmark,
    titulo: 'O que a carteira diz',
    subtitulo:
      'Leituras sobre a posição mais recente. Todas nomeiam relação — quem conclui é quem lê.',
    vazio:
      'Nenhuma leitura ainda. Ou a carteira não tem posição importada, ou as empresas dela ainda ' +
      'não foram enriquecidas pela Receita — sem o quadro societário não há grupo econômico a ver.',
    fracos: {
      rotulo: (n) => `Podem ter outra explicação (${n})`,
      explicacao:
        'Títulos de mesmo valor entre as mesmas partes costumam ser parcelas do mesmo contrato — ' +
        'o caso mais comum e inteiramente normal. Isto é par para conferir, não afirmação.',
    },
    // A via é a ORIGEM da leitura, e ela separa o que é registro do que depende de uma régua.
    vias: {
      societario: { rotulo: 'vínculo societário', classe: 'border-success/40 text-success' },
      receita_federal: { rotulo: 'Receita Federal', classe: 'border-success/40 text-success' },
      nucleo: { rotulo: 'grupo econômico', classe: 'border-border text-text-secondary' },
      concentracao: { rotulo: 'concentração', classe: 'border-border text-text-secondary' },
      mediana_da_posicao: {
        rotulo: 'mediana da posição',
        classe: 'border-border text-text-secondary',
      },
      valor_repetido: { rotulo: 'valor repetido', classe: 'border-warning/40 text-warning' },
    },
  },
};
